import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Calculator, Loader2, TrendingDown, CheckCircle2, ArrowRight, ExternalLink, ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";
import api from "@/lib/api";

export default function Migration() {
  const [catalog, setCatalog] = useState(null);
  const [included, setIncluded] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [searchParams] = useSearchParams();
  const [prefilled, setPrefilled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goBack = () => {
    // navigate(-1) when we arrived from somewhere in-app, else a sensible home.
    if (location.key && location.key !== "default") navigate(-1);
    else navigate("/command");
  };

  useEffect(() => {
    api.get("/vendors").then((res) => {
      setCatalog(res.data.catalog);
      setIncluded(res.data.migrationIncluded || []);
    }).catch(() => {});
  }, []);

  // Pre-fill from ?website_host=&live_chat=&booking=… (from a scan's detected stack).
  useEffect(() => {
    if (!catalog || prefilled) return;
    const next = {};
    for (const key of Object.keys(catalog)) {
      const raw = searchParams.get(key);
      if (!raw) continue;
      const vendors = catalog[key].vendors || [];
      const match = vendors.find(
        (v) => v.name.toLowerCase().includes(raw.toLowerCase()) || raw.toLowerCase().includes(v.name.toLowerCase()),
      );
      next[key] = match ? match.name : "other";
    }
    if (Object.keys(next).length) { setAnswers((a) => ({ ...a, ...next })); }
    setPrefilled(true);
  }, [catalog, prefilled, searchParams]);

  const setAnswer = (key, val) => setAnswers((a) => ({ ...a, [key]: val }));

  const calculate = async () => {
    setLoading(true); setResult(null);
    try {
      const res = await api.post("/migration/estimate", { answers });
      setResult(res.data);
      if (res.data.savingsAnnual > 0) toast.success(`Potential savings: $${res.data.savingsAnnual.toLocaleString()}/yr`);
    } catch (e) {
      toast.error("Could not calculate");
    } finally { setLoading(false); }
  };

  const fmt = (n) => `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

  return (
    <Layout>
      <div className="mb-6">
        <button
          data-testid="migration-back-btn"
          onClick={goBack}
          className="mb-4 inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3.5 py-1.5 text-sm text-zinc-300 hover:text-white hover:bg-white/10 transition-colors"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <h1 className="font-heading text-2xl font-bold text-white flex items-center gap-2"><Calculator size={22} className="text-[#4da3ff]" /> Migration & Savings Calculator</h1>
        <p className="text-zinc-500 text-sm mt-1">Tell us your current stack and we'll estimate how much you could save by consolidating onto one platform. You're not just buying hosting — you're buying simplification.</p>
        {prefilled && Object.keys(answers).length > 0 && (
          <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-[#4da3ff]/30 bg-[#4da3ff]/10 px-3 py-1.5 text-sm text-[#9ecbff]" data-testid="prefill-hint">
            <Calculator size={14} /> Pre-filled from your scan — adjust anything, then calculate.
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Questionnaire */}
        <div className="lg:col-span-2 bg-[#121212] border border-white/10 rounded-xl p-5 h-fit" data-testid="migration-form">
          <h2 className="font-heading text-lg font-semibold text-white mb-4">Your current providers</h2>
          <div className="space-y-3">
            {catalog && Object.entries(catalog).map(([key, cat]) => (
              <div key={key}>
                <label className="text-xs text-zinc-400 block mb-1">{cat.label}</label>
                <select
                  data-testid={`select-${key}`}
                  value={answers[key] || ""}
                  onChange={(e) => setAnswer(key, e.target.value)}
                  className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[#007AFF]/50 focus:outline-none"
                >
                  <option value="">None / not sure</option>
                  {cat.vendors.map((v) => <option key={v.name} value={v.name}>{v.name}</option>)}
                  <option value="other">Other</option>
                </select>
              </div>
            ))}
          </div>
          <button onClick={calculate} disabled={loading || !catalog} className="mt-5 w-full flex items-center justify-center gap-2 btn-brand disabled:opacity-50 text-white rounded-lg px-5 py-2.5 font-medium transition-colors" data-testid="calculate-btn">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Calculating…</> : <><TrendingDown size={16} /> Calculate My Savings</>}
          </button>
          <p className="text-[11px] text-zinc-600 mt-3">Payment processing (Stripe, etc.) is kept as-is — it's usage-based, not a fixed subscription.</p>
        </div>

        {/* Result */}
        <div className="lg:col-span-3">
          {!result ? (
            <div className="bg-[#121212] border border-white/10 rounded-xl p-10 text-center text-zinc-500 h-full flex flex-col items-center justify-center" data-testid="migration-empty">
              <Calculator size={36} className="text-zinc-700 mb-3" />
              Fill in your current stack and calculate to see your estimated annual savings.
            </div>
          ) : (
            <div className="space-y-5" data-testid="migration-result">
              <div className="rounded-xl p-6 text-center" style={{ background: "linear-gradient(135deg, rgba(0,122,255,0.15), rgba(155,92,255,0.15))", border: "1px solid rgba(0,122,255,0.3)" }}>
                <div className="text-xs uppercase tracking-widest text-zinc-400">Potential annual savings</div>
                <div className="text-5xl font-heading font-extrabold mt-2 brand-gradient" data-testid="savings-amount">
                  {result.savingsAnnual > 0 ? fmt(result.savingsAnnual) : "Streamlined"}
                </div>
                <div className="flex items-center justify-center gap-3 mt-3 text-sm text-zinc-300">
                  <span>Now: <b className="text-white">{fmt(result.currentAnnual)}/yr</b></span>
                  <ArrowRight size={14} className="text-[#4da3ff]" />
                  <span>On Emergent: <b className="text-white">{fmt(result.proposedAnnual)}/yr</b></span>
                </div>
              </div>

              <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
                <h3 className="font-heading font-semibold text-white mb-3">Breakdown</h3>
                <div className="overflow-x-auto rounded-lg border border-white/10">
                  <table className="w-full text-sm">
                    <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wide">
                      <tr><th className="text-left px-3 py-2">Category</th><th className="text-left px-3 py-2">Now</th><th className="text-left px-3 py-2">Recommended</th><th className="text-right px-3 py-2">Now → New /mo</th></tr>
                    </thead>
                    <tbody>
                      {result.breakdown.map((b, i) => (
                        <tr key={i} className="border-t border-white/5" data-testid={`breakdown-${i}`}>
                          <td className="px-3 py-2 text-zinc-300">{b.category}</td>
                          <td className="px-3 py-2 text-zinc-400">{b.current}</td>
                          <td className="px-3 py-2">
                            {b.recommended ? (
                              b.recommended.url ? (
                                <a href={b.recommended.url} target="_blank" rel="noreferrer" className="text-[#4da3ff] hover:text-white inline-flex items-center gap-1">
                                  {b.recommended.name} <ExternalLink size={11} />
                                </a>
                              ) : (
                                <span className="text-zinc-300">{b.recommended.name}</span>
                              )
                            ) : "—"}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-zinc-300">
                            {fmt(b.current_monthly)} → {b.folds ? <span className="text-[#30D158]">Included</span> : fmt(b.proposed_monthly)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-zinc-600 mt-2">Estimates based on typical published plan prices; your actual costs may vary.</p>
              </div>

              <div className="bg-[#121212] border border-white/10 rounded-xl p-5">
                <h3 className="font-heading font-semibold text-white mb-3">Website Migration Included</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {(result.migrationIncluded || included).map((item, i) => (
                    <div key={i} className="flex items-center gap-2 text-sm text-zinc-300" data-testid={`included-${i}`}>
                      <CheckCircle2 size={15} className="text-[#30D158] shrink-0" /> {item}
                    </div>
                  ))}
                </div>
                <a href={result.emergentUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 btn-brand text-white rounded-full px-5 py-2.5 font-medium" data-testid="build-emergent-cta">
                  Build it on Emergent <ArrowRight size={16} />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
